import React, { useMemo, useState } from 'react';
import { groupByMonthKey, foldRows, loggedByIfNotOwner } from '../lib/activityGroups.js';
import { loggedByLabel } from '../lib/activityExport.js';

// The admin ledger's body (owner readability pass, 2026-09-23):
//   · a real table on wide screens — Date · Investor · Route · Amount · Value
//     · Split · Logged by — that restacks into the card layout on phones
//   · the investor leads: household bold + colour, sub-account after it
//   · nothing that is true of almost every row: no "Kareem holds" (pool is
//     the norm), no "logged by" when it is the owner, no $0 split pills
//   · months collapse with their subtotal in the header (latest month open),
//     50 rows a page inside a month
//   · identical same-day draws by one investor fold into one line
// Rows are `.activity-row` so counts and the mobile treatment carry over.

const fmt$ = (n, d = 0) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const v = Math.abs(n);
  return (n < 0 ? '-' : '') + '$' + v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmtPct = (n) => parseFloat((Number(n || 0) * 100).toFixed(3)).toString() + '%';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (key) => {
  const m = /^(\d{4})-(\d{2})$/.exec(key || '');
  return m ? MONTHS[Number(m[2]) - 1] + ' ' + m[1] : 'Earlier';
};
const PAGE = 50;

export function AdminLedgerBody({ rows, byId, onRowClick, pageSize = PAGE }) {
  const months = useMemo(() => groupByMonthKey(rows), [rows]);
  // Latest month open by default; the rest collapsed behind their subtotal.
  const [closed, setClosed] = useState(() => new Set());
  const [opened, setOpened] = useState(() => new Set());
  const [shown, setShown] = useState({});
  const [openFolds, setOpenFolds] = useState(() => new Set());
  const isOpen = (key, idx) => (idx === 0 ? !closed.has(key) : opened.has(key));
  const toggleMonth = (key, idx) => {
    if (idx === 0) setClosed((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
    else setOpened((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };
  const toggleFold = (id) => setOpenFolds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const person = (id) => byId[id] || { id, name: id || '?', initials: '?', color: '#94a3b8' };

  const renderRow = (e, { child = false } = {}) => {
    const owner = person(e.member);
    const hh = person(e.household || e.member);
    const isSub = hh.id !== owner.id;
    const folded = Array.isArray(e.fold);
    const open = folded && openFolds.has(e.id);
    const logged = e.mixedLogger ? 'mixed' : loggedByIfNotOwner(loggedByLabel(e, byId), owner.name);
    const clickable = folded ? true : !!onRowClick;
    const onClick = folded ? () => toggleFold(e.id) : (onRowClick ? () => onRowClick(e) : undefined);
    const title = folded
      ? (open ? 'Collapse' : `Show the ${e.count} draws`)
      : (onRowClick ? `Open ${hh.name}'s activity` : undefined);
    return (
      <React.Fragment key={e.id}>
        <div
          className={'activity-row act-tr' + (clickable ? ' is-clickable' : '') + (folded ? ' is-fold' : '') + (child ? ' is-child' : '')}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          title={title}
          onClick={onClick}
          onKeyDown={clickable ? (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onClick(); } } : undefined}
        >
          <div className="act-td act-td-date">{e.date}</div>
          <div className="act-td act-td-who">
            <div className="avatar act-avatar" style={{ background: hh.color }}>{hh.initials}</div>
            <div className="act-who-names">
              <div className="act-who-date">{e.date}</div>
              <div className="act-who-hh">{hh.name}</div>
              {isSub ? <div className="act-who-sub">{owner.name}</div> : null}
            </div>
          </div>
          <div className="act-td act-td-route">
            <span className="act-route">{e.flow}</span>
            {folded ? <span className="act-chip act-chip-fold">{open ? '▾' : '▸'} {e.count} draws</span> : null}
            {e.settleType ? <span className={'ar-pending ar-settle' + (e.settleType === 'payout' ? ' ar-settle-payout' : '')}>Settlement</span> : null}
            {e.movementStatus === 'pending' ? <span className="ar-pending">Awaiting Kareem</span> : null}
            {e.custody === 'investor' && Number(e.value || 0) !== 0 ? <span className="act-chip act-chip-inv">Investor holds</span> : null}
          </div>
          <div className="act-td act-td-amt">
            <span className="mono">{fmt$(e.amount)}</span>
            {e.noValue
              ? <span className="act-muted"> · no value</span>
              : e.mixedRate
                ? <span className="act-muted"> · mixed rates</span>
                : <span className="act-muted"> @ {fmtPct(e.rate)}</span>}
          </div>
          <div className={'act-td act-td-val' + (Number(e.value || 0) === 0 ? ' is-zero' : '')}>
            {Number(e.value || 0) === 0 ? '$0' : '+' + fmt$(e.value)}
          </div>
          <div className="act-td act-td-split">
            {Number(e.value || 0) !== 0 ? (
              <>
                <span className="split-pill kareem">K {fmt$(e.kareem)}</span>
                <span className="split-pill investor">Inv {fmt$(e.investor)}</span>
              </>
            ) : null}
          </div>
          <div className="act-td act-td-logged">{logged ? <span className="act-logged-by">{logged}</span> : null}</div>
        </div>
        {folded && open ? e.fold.map((c) => renderRow(c, { child: true })) : null}
      </React.Fragment>
    );
  };

  if (!months.length) return null;

  return (
    <div className="act-table">
      <div className="act-thead" aria-hidden="true">
        <div className="act-th act-td-date">Date</div>
        <div className="act-th act-td-who">Investor</div>
        <div className="act-th act-td-route">Route</div>
        <div className="act-th act-td-amt">Amount</div>
        <div className="act-th act-td-val">Value</div>
        <div className="act-th act-td-split">Split</div>
        <div className="act-th act-td-logged">Logged by</div>
      </div>
      {months.map((m, idx) => {
        const open = isOpen(m.key, idx);
        const foldedRows = open ? foldRows(m.rows) : [];
        const limit = shown[m.key] || pageSize;
        const visible = foldedRows.slice(0, limit);
        const left = foldedRows.length - visible.length;
        return (
          <section key={m.key} className={'act-month' + (open ? ' is-open' : '')}>
            <button type="button" className="act-month-head act-month-toggle" onClick={() => toggleMonth(m.key, idx)} aria-expanded={open}>
              <span className="act-month-caret">{open ? '▾' : '▸'}</span>
              <span className="act-month-name">{monthLabel(m.key)}</span>
              <span className="act-month-sub">
                {m.totals.count} row{m.totals.count === 1 ? '' : 's'}
                <span className="act-sum-sep">·</span>{fmt$(m.totals.amount)} moved
                <span className="act-sum-sep">·</span>{m.totals.value === 0 ? '$0' : '+' + fmt$(m.totals.value)} value
              </span>
            </button>
            {open ? (
              <div className="activity-list act-tbody">
                {visible.map((e) => renderRow(e))}
                {left > 0 ? (
                  <button type="button" className="act-more" onClick={() => setShown((s) => ({ ...s, [m.key]: limit + pageSize }))}>
                    Show {Math.min(pageSize, left)} more <span className="act-muted">({left} left in {monthLabel(m.key)})</span>
                  </button>
                ) : null}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}

export default AdminLedgerBody;
